import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { ChevronLeft, RefreshCw, Loader2 } from 'lucide-react';
import { scaleIn, fadeInUp } from '../utils/animations';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        setLoading(true);

        // Get query parameters from URL
        const queryParams = new URLSearchParams(location.search);
        const orderId = queryParams.get('orderId');

        if (!orderId) {
          setError('Informații lipsă despre plată.');
          return;
        }

        // Get current user
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) {
          navigate('/login');
          return;
        }

        // Poll the payment status with retries
        let retries = 0;
        const maxRetries = 10;
        const retryDelay = 2000; // 2 seconds

          while (retries < maxRetries) {
          // Check payment status from Supabase including webhook_sent and discount_code
          const { data: paymentAttempt, error: fetchError } = await supabase
            .from('payment_attempts')
            .select('status, payment_data, amount, webhook_sent, discount_code')
            .eq('order_id', orderId)
            .single();

          if (fetchError) throw fetchError;

          if (paymentAttempt?.status === 'completed') {
            // If webhook already sent, skip processing and just set success
            if (paymentAttempt.webhook_sent) {
              setSuccess(true);
              return;
            }
            // Get user profile data including current consultation_status
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select(
                'email, prenume, nume, cnp, telefon, judet, localitate, consultation_status'
              )
              .eq('id', user.id)
              .single();

            if (profileError) throw profileError;

            // Send webhook to make.com
            const webhookResponse = await fetch(
              'https://n8n.ovi.net.eu.org/webhook/generare-factura',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  email: profile.email,
                  prenume: profile.prenume,
                  nume: profile.nume,
                  cnp: profile.cnp,
                  localitate: profile.localitate,
                  judet: profile.judet,
                  telefon: profile.telefon || '',
                  amount: paymentAttempt.amount,
                  timestamp: new Date().toISOString(),
                  discount: paymentAttempt.discount_code || 'none',
                }),
              }
            );

            if (!webhookResponse.ok) {
              console.error(
                'Failed to send webhook:',
                await webhookResponse.text()
              );
            }

            // Update webhook_sent to true after sending webhook
            const { error: webhookUpdateError } = await supabase
              .from('payment_attempts')
              .update({ webhook_sent: true })
              .eq('order_id', orderId);

            if (webhookUpdateError) {
              console.error(
                'Failed to update webhook_sent:',
                webhookUpdateError
              );
            }

            // Only update profile status if not already paid or planned
            if (
              profile.consultation_status !== 'paid' &&
              profile.consultation_status !== 'planned'
            ) {
              // Determine service type based on payment amount
              const serviciuType =
                paymentAttempt.amount === 900 ? 'asociatie' : 'standard';

              const { error: updateError } = await supabase
                .from('profiles')
                .update({
                  consultation_status: 'paid',
                  serviciu: serviciuType,
                })
                .eq('id', user.id);

              if (updateError) throw updateError;
            }

            setSuccess(true);
            return;
          } else if (paymentAttempt?.status === 'failed') {
            throw new Error(
              paymentAttempt.payment_data?.message || 'Payment failed'
            );
          }

          // Wait before next retry
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          retries++;
        }

        throw new Error('Payment verification timeout');
      } catch (err) {
        console.error('Error checking payment status:', err);
        setError('A apărut o eroare la verificarea plății.');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [location, navigate]);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
      </div>

      <motion.div
        className="relative bg-white rounded-xl shadow-lg max-w-lg w-full p-8 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {loading ? (
          <motion.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-12 h-12 text-[#024994] animate-spin" />
            <p className="text-gray-600">Se verifică statusul plății...</p>
          </motion.div>
        ) : error ? (
          <>
            <motion.div
              className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.div>
            <motion.h1
              className="text-2xl font-bold text-gray-900 mb-4"
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              Eroare la procesarea plății
            </motion.h1>
            <motion.p
              className="text-gray-600 mb-8"
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              {error}
            </motion.p>
          </>
        ) : success ? (
          <>
            <motion.div
              className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
            <motion.h1
              className="text-2xl font-bold text-gray-900 mb-4"
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              Plată finalizată cu succes!
            </motion.h1>
            <motion.p
              className="text-gray-600 mb-8"
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              Vă mulțumim pentru plată. Consultația dumneavoastră a fost
              înregistrată. Dacă ați încărcat toate documentele medicale vă
              rugăm să apăsați pe butonul "Solicită programare"
            </motion.p>
          </>
        ) : null}

        <motion.button
          onClick={() => navigate('/pacient')}
          className="flex items-center justify-center gap-2 mx-auto px-6 py-3 bg-[#024994] text-white rounded-lg hover:opacity-90 transition"
          disabled={loading}
        >
          <ChevronLeft className="w-5 h-5" />
          {loading ? 'Se procesează...' : 'Înapoi la consultații'}
        </motion.button>
      </motion.div>
    </div>
  );
}
